import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Server, RefreshCw, Loader2, AlertTriangle, Database } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button } from '../../Components/ui';
import ComparisonBar from '../../Components/ComparisonBar';
import { API_URL } from '../../api/config';

// Tela só master — acompanha "recursos do sistema" (infraestrutura, não dado
// de instituição nenhuma), por isso não usa useInstitution/x-institution-id,
// mesmo espírito de EstatisticasComparativas.js. Começa só com a saúde do
// pool de conexões MySQL (ver backend/db-health.js); pensada pra crescer —
// cada recurso novo é só mais um <Card> aqui dentro.
// Extrai HH:mm:ss de "YYYY-MM-DD HH:mm:ss" via slice, nunca via `new
// Date(iso).toLocaleTimeString()` — isso leria a hora de volta no fuso do
// computador de quem está vendo a tela, mesmo problema já visto em
// Pontos.js (ver formatarHora lá). O valor já vem certo (Brasília) do
// backend, ver db-health.js/agoraBrasil().
const formatarHora = (iso) => (iso ? iso.slice(11, 19) : null);

const STATUS_INFO = {
  ok: {
    label: 'Normal',
    light: '#86EFAC',
    dark: '#16A34A',
    badge: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
  },
  atencao: {
    label: 'Atenção',
    light: '#FCD34D',
    dark: '#D97706',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  },
  critico: {
    label: 'Crítico',
    light: '#FCA5A5',
    dark: '#DC2626',
    badge: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  },
};

function RecursosSistema() {
  const { token } = useAuth();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['db-health'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/db-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar saúde do banco.');
      return json?.data ?? json;
    },
    enabled: !!token,
    refetchInterval: 30000,
  });

  const info = STATUS_INFO[data?.status] || STATUS_INFO.ok;

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-600" /> Recursos do Sistema
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Métricas de infraestrutura — atualiza sozinho a cada 30s.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshCw}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-3" /> Carregando...
          </div>
        ) : error ? (
          <Card>
            <div className="text-center py-8 text-red-500">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">{error.message}</p>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Database className="w-4 h-4 text-gray-400" /> Banco de Dados (MySQL)
              </h3>
              <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${info.badge}`}>
                {info.label}
              </span>
            </div>

            <ComparisonBar
              label="Conexões"
              value={data.threads_connected}
              maxValue={data.max_connections}
              colorLight={info.light}
              colorDark={info.dark}
              suffix={` / ${data.max_connections}`}
            />

            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-50 dark:border-gray-800">
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500">Uso atual</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {data.pct_atual}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500">Pico histórico</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {data.max_used_connections} conexões
                </p>
              </div>
            </div>

            {data?.atualizado_em && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-4">
                Última atualização: {formatarHora(data.atualizado_em)}
              </p>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}

export default RecursosSistema;
